<template>
    <div class="vrm-search-container--enhanced">
        <div class="vrm-search--enhanced" data-cy="enhanced-search-input" @click="showActiveSearch">
            <div class="vrm-search__input-container">
                <div class="vrm-search__placeholder--enhanced">
                    <span>
                        {{
                            searchTermForInput.length > 0
                                ? getSearchTermForInput(searchTermForInput)
                                : $t('nav.enhanced_search.placeholder', {
                                      i18nFallback: 'Search installations',
                                  })
                        }}
                    </span>
                </div>
                <div v-if="!isMobile && isInputEmpty">
                    <span v-if="isMacOs" class="vrm-search__command-icon">&#8984;&nbsp;K</span>
                    <span v-else class="vrm-search__command-icon">Ctrl&nbsp;K</span>
                </div>
            </div>
            <div class="vrm-search__input-container">
                <div class="separator"></div>
                <span class="sg-vrm-icon_121_search-24px vrm-search__icon--enhanced"></span>
            </div>
        </div>
        <transition name="fade">
            <VrmEnhancedSearchActive
                v-show="activeSearch"
                ref="childRef"
                :search-results="searchResults"
                :loading="loading"
                :no-results="noResults"
                :hits-number="numberOfHits"
                :search-error="searchError"
                @on-close-search="handleCloseSearch"
                @on-search="getSearchResults"
            />
        </transition>
    </div>
</template>

<script>
import { Vue, Component, Watch } from 'vue-property-decorator';
import VrmEnhancedSearchActive from './VrmEnhancedSearchActive.vue';
import { AngularDirective, ngInject } from '@/vue/decorators/angular-interop';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';

@Component({
    components: { VrmEnhancedSearchActive },
    emits: ['search'],
})
@AngularLocaleBundle('nav')
@AngularDirective('vrm-enhanced-search')
export default class VrmEnhancedSearch extends Vue {
    @ngInject()
    ApiService;

    activeSearch = false;
    searchResults = [];
    searchTermForInput = ''; // Value of the background input field
    loading = false;
    noResults = false;
    numberOfHits = 0;
    searchError = false;

    created() {
        // Add the global keydown listener
        window.addEventListener('keydown', this.handleKeydown);
    }

    showActiveSearch() {
        this.activeSearch = true;
    }

    handleCloseSearch(searchTerm) {
        if (searchTerm === '') {
            this.getSearchResults({ searchTerm });
        }
        this.searchTermForInput = searchTerm;
        this.activeSearch = false;
    }

    getSearchResults({ searchTerm }) {
        this.searchError = false;
        searchTerm = searchTerm.trim();
        this.searchTermForInput = searchTerm;
        this.loading = true;

        if (searchTerm === '') {
            this.loading = false;
            this.searchResults = [];
            this.noResults = false;
            this.$emit('search', []);
            return;
        }

        this.ApiService.getInstallationSearchResults(encodeURIComponent(searchTerm))
            .then(response => {
                this.searchResults = response.results;
                this.numberOfHits = response.count;
                this.loading = false;
                this.noResults = this.searchResults.length === 0;
                this.$emit(
                    'search',
                    this.searchResults.map(installation => installation.site_id),
                );
                if (this.searchResults.length > 0) {
                    this.$nextTick(() => this.$refs.childRef.changeFocus(0));
                }
            })
            .catch(() => {
                this.loading = false;
                this.searchError = true;
                this.noResults = true;
                this.searchResults = [];
            });
    }

    handleKeydown(event) {
        if ((event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === '/')) {
            event.preventDefault();
            if (this.activeSearch) {
                this.$nextTick(() => {
                    this.$el.querySelector('.vrm-search__input--enhanced--active').focus(); // Focus the input field that shows up
                });
            } else {
                this.activeSearch = true;
            }
        }
    }

    @Watch('activeSearch')
    onActiveSearchChange() {
        if (this.activeSearch) {
            if (document.body.scrollHeight > window.innerHeight) {
                document.documentElement.classList.add('no-scroll');
            }
            this.$nextTick(() => {
                this.$el.querySelector('.vrm-search__input--enhanced--active').focus(); // Focus the input field that shows up
            });
        } else {
            if (document.documentElement.classList.contains('no-scroll')) {
                document.documentElement.classList.remove('no-scroll');
            }
        }
    }

    get isInputEmpty() {
        return this.searchTermForInput === '';
    }

    get isMacOs() {
        if (navigator.userAgentData && navigator.userAgentData.platform) {
            return navigator.userAgentData.platform.toLowerCase().includes('mac');
        }
        // Fallback for browsers like Firefox
        return navigator.userAgent.toLowerCase().includes('mac');
    }

    get isMobile() {
        return window.innerWidth <= 750;
    }

    getSearchTermForInput(searchTerm) {
        return searchTerm.length > 25 ? `${searchTerm.substring(0, 25)}...` : searchTerm;
    }

    beforeDestroy() {
        document.documentElement.classList.remove('no-scroll');

        window.removeEventListener('keydown', this.handleKeydown);
        document.body.style.overflow = '';
    }
}
</script>
